<template>
    <b-modal id="text-notification-config-modal" title="Configure Text Notifications" @show="loadPreferences"
        @ok="savePreferences" ok-title="Save" no-fade>
        <b-alert v-if="textOptedOut" show variant="warning" class="mb-3">
            This patient has opted out of all text messages. Individual preferences below will only take effect
            if the patient opts back in.
        </b-alert>

        <div v-if="loading" class="text-center py-3">
            <b-spinner small /> Loading preferences...
        </div>

        <div v-else>
            <p class="text-muted small mb-3">
                Uncheck a notification type to disable it for this patient.
            </p>
            <div v-for="(label, key) in enabledNotificationTypeLabels" :key="key" class="mb-2">
                <b-form-checkbox v-model="preferences[key]" switch>
                    {{ label }}
                </b-form-checkbox>
            </div>
            <p v-if="allDisabledAtProgramLevel" class="text-muted">
                All notification types are disabled at the program/configuration level.
            </p>
        </div>

        <b-alert v-if="error" show variant="danger" class="mt-3 mb-0">{{ error }}</b-alert>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import axios from 'axios';
import { notificationTypeLabels } from '@/models/PatientTextNotificationPreference';

@Component
export default class PatientTextNotificationPreferencesModal extends Vue {
    @Prop({ required: true }) patientId!: number;
    @Prop({ default: null }) textMessageTemplates!: any;
    @Prop({ default: false }) textOptedOut!: boolean;

    protected loading = false;
    protected error = '';
    protected preferences: Record<string, boolean> = {};

    async loadPreferences() {
        this.loading = true;
        this.error = '';

        // Initialize all as enabled (not disabled)
        const prefs: Record<string, boolean> = {};
        for (const key of Object.keys(notificationTypeLabels)) {
            prefs[key] = true;
        }
        this.preferences = prefs;

        try {
            const resp = await axios.get(`/api/Patient/${this.patientId}/TextNotificationPreferences`);
            if (resp.data) {
                for (const key of Object.keys(notificationTypeLabels)) {
                    if (resp.data[key]?.Disabled === true) {
                        prefs[key] = false;
                    }
                }
                this.preferences = prefs;
            }
        } catch (err) {
            console.error("Failed to load text notification preferences:", err);
        } finally {
            this.loading = false;
        }
    }

    get enabledNotificationTypeLabels(): Record<string, string> {
        const result: Record<string, string> = {};
        for (const [key, label] of Object.entries(notificationTypeLabels)) {
            if (!this.isDisabledAtProgramLevel(key)) {
                result[key] = label;
            }
        }
        return result;
    }

    get allDisabledAtProgramLevel(): boolean {
        return Object.keys(this.enabledNotificationTypeLabels).length === 0;
    }

    isDisabledAtProgramLevel(key: string): boolean {
        if (!this.textMessageTemplates) return false;
        return this.textMessageTemplates[key]?.Disabled === true;
    }

    async savePreferences(event: any) {
        event.preventDefault();
        this.error = '';

        const payload: Record<string, { Disabled: boolean }> = {};
        for (const [key, enabled] of Object.entries(this.preferences)) {
            if (!enabled) {
                payload[key] = { Disabled: true };
            }
        }

        try {
            await axios.post(`/api/Patient/${this.patientId}/TextNotificationPreferences`, payload);
            this.$nextTick(() => {
                this.$bvModal.hide('text-notification-config-modal');
            });
        } catch (err: any) {
            this.error = err?.response?.data || 'Failed to save preferences';
        }
    }
}
</script>
